import { useState } from 'react';

import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';
import { selectUserUID } from 'client/redux/client/selectors';
import { setMicrophoneError } from 'client/redux/mux-details';
import { getToken } from 'client/redux/mux-details/actions';
import { selectRoomId } from 'client/redux/room/selectors';
import { CONFIG } from 'constants/index';

import { useToggleMicrophone } from './use-toggle-microphone';
import { useAppDispatch, useAppSelector } from './redux';

export const useMuxSpace = () => {
  const userUid = useAppSelector(selectUserUID);
  const [isBusy, setIsBusy] = useState(false);
  const { muteMic, unMuteMic } = useToggleMicrophone();
  const { state, dispatch } = useLiveStreamContext();
  const appDispatch = useAppDispatch();
  const roomId = useAppSelector(selectRoomId);

  const join = async (options: { canPublish: boolean; micInitialState: 'muted' | 'unmuted' }) => {
    try {
      if (!state.space) {
        console.info('no space');
        return;
      }

      setIsBusy(true);

      if (state.localParticipant && state.space.state === 'connected') {
        // it means host give permissions to speak to some listener(who is already a participant of the room, just without canPublish permissions)
        // listener permissions has been changed to canPublish: true

        console.info('Show Join to space popup to listener who has canPublish permissions');
        if (state.space.localParticipant.permissions?.canPublish) {
          // publish local mic track
          await state.space.localParticipant.setMicrophoneEnabled(true);

          if (options.micInitialState === 'muted') {
            muteMic();
          }

          if (options.micInitialState === 'unmuted') {
            unMuteMic();
          }

          setIsBusy(false);
        }
      } else {
        console.info('Room join local participant');

        const response = await appDispatch(
          getToken({
            canPublishTracks: options.canPublish,
            userId: userUid,
            roomId: roomId,
          }),
        ).unwrap();
        console.info('response', response);
        const token = response.success ? response.token : undefined;

        if (!token) {
          console.info('no token');
          setIsBusy(false);
          return { CODE: 'GET_TOKEN_REQUEST_ERROR' } as const;
        }

        await state.space.connect(CONFIG.liveKitHostUrl, token);

        dispatch({
          type: 'SET_PARTICIPANTS',
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          payload: Array.from(state.space.participants, ([_, value]) => value).filter((p) =>
            Boolean(p.permissions?.canPublish),
          ),
        });
        dispatch({ type: 'SET_LOCAL_PARTICIPANT', payload: state.space.localParticipant });

        // we don't need to publish tracks from users who don't have permissions to publish their tracks(admin and listeners)
        if (state.space.localParticipant.permissions?.canPublish) {
          // publish local mic track
          await state.space.localParticipant.setMicrophoneEnabled(true);

          if (options.micInitialState === 'muted') {
            muteMic();
          }

          if (options.micInitialState === 'unmuted') {
            unMuteMic();
          }
        }

        setIsBusy(false);
      }
    } catch (e) {
      setIsBusy(false);
      console.info('useSpace error', e);

      if (
        (e as Error).message.includes('Permission denied') ||
        (e as Error).message.includes('user denied permission')
      ) {
        console.info('microphone issue');
        appDispatch(
          setMicrophoneError(
            'Seems like you have permissions restriction for your microphone in the device settings. Go to system preferences, change permissions and reload the page',
          ),
        );
      }
    }
  };

  return { join, isBusy };
};
